import type {
  SubmissionsExportColumns,
  SubmissionsExportFieldColumn,
  SubmissionsExportMetaColumn,
} from "../../types/submission";
import { isDisplayFieldType } from "../formBuilder/apiFieldTypes";
import { isSubmissionsExportMetaKey } from "./submissionsExport";
import { normalizeStaffColumns } from "./staffColumns";

function normalizeMetaColumn(raw: unknown): SubmissionsExportMetaColumn | null {
  if (!raw || typeof raw !== "object") return null;

  const item = raw as Record<string, unknown>;
  const key =
    typeof item.key === "string" && isSubmissionsExportMetaKey(item.key)
      ? item.key
      : null;
  if (!key) return null;

  const label = typeof item.label === "string" ? item.label.trim() : "";
  return {
    key,
    label: label || key,
  };
}

function cityLabelsFromExportItem(
  item: Record<string, unknown>,
): Record<string, string> {
  const labels: Record<string, string> = {};
  const cityBlock =
    item.city && typeof item.city === "object"
      ? (item.city as Record<string, unknown>)
      : null;
  const optionsBlock =
    item.options && typeof item.options === "object"
      ? (item.options as Record<string, unknown>)
      : null;

  const choices =
    (cityBlock && Array.isArray(cityBlock.choices)
      ? cityBlock.choices
      : null) ??
    (optionsBlock && Array.isArray(optionsBlock.choices)
      ? optionsBlock.choices
      : null);

  if (!Array.isArray(choices)) return labels;

  for (const choice of choices) {
    if (typeof choice === "string") continue;
    if (
      choice &&
      typeof choice === "object" &&
      "value" in choice &&
      choice.value != null
    ) {
      const key = String(choice.value);
      const label = String(
        "label" in choice ? (choice.label ?? choice.value) : choice.value,
      ).trim();
      if (key && label) labels[key] = label;
    }
  }

  return labels;
}

function normalizeFieldColumn(
  raw: unknown,
): SubmissionsExportFieldColumn | null {
  if (!raw || typeof raw !== "object") return null;

  const item = raw as Record<string, unknown>;
  const idRaw = item.id;
  const id =
    typeof idRaw === "number"
      ? idRaw
      : typeof idRaw === "string" && idRaw !== ""
        ? Number(idRaw)
        : NaN;
  if (!Number.isFinite(id)) return null;

  const label =
    typeof item.label === "string" && item.label.trim()
      ? item.label.trim()
      : typeof item.name === "string" && item.name.trim()
        ? item.name.trim()
        : `Поле #${Math.trunc(id)}`;
  const name = typeof item.name === "string" ? item.name : undefined;
  const type =
    typeof item.type === "string" && item.type.trim()
      ? item.type.trim()
      : undefined;
  if (isDisplayFieldType(type)) return null;
  const order =
    typeof item.order === "number" && Number.isFinite(item.order)
      ? Math.trunc(item.order)
      : undefined;
  const cityLabels = cityLabelsFromExportItem(item);

  return {
    id: Math.trunc(id),
    label,
    name,
    type,
    order,
    cityLabels: Object.keys(cityLabels).length > 0 ? cityLabels : undefined,
  };
}

export function normalizeSubmissionsExportColumns(
  raw: unknown,
): SubmissionsExportColumns {
  if (!raw || typeof raw !== "object") {
    return { meta: [], fields: [], staffColumns: [] };
  }

  const item = raw as Record<string, unknown>;
  const meta = Array.isArray(item.meta)
    ? item.meta
        .map(normalizeMetaColumn)
        .filter(
          (column): column is SubmissionsExportMetaColumn => column != null,
        )
    : [];
  const fields = Array.isArray(item.fields)
    ? item.fields
        .map(normalizeFieldColumn)
        .filter(
          (column): column is SubmissionsExportFieldColumn => column != null,
        )
    : [];

  return {
    meta,
    fields,
    staffColumns: normalizeStaffColumns(
      item.staffColumns ?? item.staff_columns,
    ),
  };
}
